import type { TPlayerDiagnosticFault, TPlayerDiagnosticsSnapshot } from '@lucimark/shared/models/player-diagnostics'
import type { IPlaybackTimerStuckStatus } from '@/helpers/detectPlaybackTimerStuck'
import type { TVisibleVideoSrcScheme } from '@/helpers/playerDiagnostics/resolveVisibleVideoSrcScheme'
import type { TMainLoopGapCause } from '@/helpers/playerDiagnostics/classifyMainLoopGap'
import { unexpectedVerticalAspectSignals } from '@lucimark/shared/helpers/playbackAspectTelemetry'
import {
  PLAYER_DIAGNOSTIC_FAULTS_MAX_LENGTH,
  PLAYER_DIAGNOSTIC_FRAME_ID_MAX_LENGTH,
  PLAYER_DIAGNOSTICS_SNAPSHOT_SCHEMA_VERSION
} from '@lucimark/shared/models/player-diagnostics'
import { getCachedAndroidProcessSelfHealStatus } from '@/helpers/androidProcessSelfHeal'
import {
  getWebRecoveryReloadCount,
  getWebRecoveryReloadLastAt
} from '@/helpers/playbackFailureReloadPolicy'
import { detectContentNotAdvancing } from '@/helpers/playerDiagnostics/detectContentNotAdvancing'
import { detectEmptyPlaybackSurface } from '@/helpers/playerDiagnostics/detectEmptyPlaybackSurface'
import { detectFrameTransitionAccelerated, type ILastCompletedFrameTransition } from '@/helpers/playerDiagnostics/detectFrameTransitionAccelerated'
import { detectFrameTransitionStall } from '@/helpers/playerDiagnostics/detectFrameTransitionStall'
import { detectSchedulerMainDesync } from '@/helpers/playerDiagnostics/detectSchedulerMainDesync'
import { detectTickCadenceDegraded } from '@/helpers/playerDiagnostics/detectTickCadenceDegraded'
import { detectVideoPaintStall } from '@/helpers/playerDiagnostics/detectVideoPaintStall'
import { peekLastPlaybackAspectTelemetrySample } from '@/helpers/observePlaybackAspectTelemetry'
import { peekPlayerGlassDiagnosticsSample } from '@/helpers/playerDiagnostics/playerGlassDiagnosticsSample'
import {
  isPlaylistSwapDiagnosticsInteresting,
  peekPlaylistSwapDiagnosticsSample
} from '@/helpers/playerDiagnostics/playlistSwapDiagnosticsSample'
import {
  getPlayerDiagnosticSessionActiveUntilMs,
  isPlayerDiagnosticsSamplingEnabled
} from '@/helpers/playerDiagnostics/playerDiagnosticsKillSwitch'
import {
  clearPlayerDiagnosticsFaultSentryDedupe,
  reportPlayerDiagnosticsFaultToSentry
} from '@/helpers/reportPlayerDiagnosticsFaultToSentry'
import { reportFrameTransitionAcceleratedToSentry } from '@/helpers/reportFrameTransitionAcceleratedToSentry'
import { clearPlayerPlaybackDurationInvariantSentryDedupe } from '@/helpers/reportPlayerPlaybackDurationInvariantToSentry'
import PlayerDiagnosticsWorker from '@/workers/player-diagnostics.worker?worker'

export interface IPlayerDiagnosticSampleInput {
  nowMs: number
  documentVisible: boolean
  queueLength: number
  /** Painted presentation-surface frame count (0 = brand cube / empty). */
  surfaceFrameCount: number
  /** True when a surface commit is deferred on the asset index. */
  pendingSurfaceCommit: boolean
  /** Continuous empty-surface stall while queue non-empty; null when surface has frames. */
  emptySurfaceStallMs: number | null
  /** On-air main facet is AssetType.Video. */
  visibleFrameExpectsVideo: boolean
  visibleVideoElementCount: number
  visibleVideoReadyState: number | null
  visibleVideoWidth: number | null
  visibleVideoNetworkState: number | null
  visibleVideoMediaErrorCode: number | null
  visibleVideoSrcScheme: TVisibleVideoSrcScheme | null
  /** Continuous video-expected / not-paint-ready window; null when not in that mode. */
  videoPaintStallMs: number | null
  /** AssetsService download progress 0–1; null when unknown / NaN. */
  assetDownloadProgress01: number | null
  visibleFrameId: string | null
  notifiedIdx: number
  /** Worker on-air index from {@link IPlaybackTiming.notifiedIdx}; `-1` when unknown. */
  workerNotifiedIdx: number
  notifiedFrameId: string | null
  frameElapsedMs: number | null
  frameDurationMs: number | null
  overlayFrameDurationMs: number | null
  mediaDurationSec: number | null
  visibleFrameStartMs: number
  lastTickAtMs: number | null
  lastFrameTransitionAtMs: number
  weeklyAvailabilitySuppressed: boolean
  playbackTimerStuck: IPlaybackTimerStuckStatus | null
  lastCompletedFrameTransition: ILastCompletedFrameTransition | null
  suppressBecauseRegistrationResync: boolean
  suppressBecauseRegistrationTransitionHold: boolean
}

interface TFaultSlot {
  code: TPlayerDiagnosticFault['code']
  active: boolean
  sinceMs: number
  clearedAtMs: number
  detailMs: number
}

const EMPTY_FAULT: TFaultSlot = {
  code: '',
  active: false,
  sinceMs: 0,
  clearedAtMs: 0,
  detailMs: 0
}

const faultSlots: TFaultSlot[] = Array.from({ length: PLAYER_DIAGNOSTIC_FAULTS_MAX_LENGTH }, () => ({ ...EMPTY_FAULT }))

/**
 * Last gap in the main-thread diagnostics poll, with the cause the worker thread witnessed (#1500).
 * Recorded when it happens rather than sampled, and kept until the next one so the heartbeat after a
 * resume still carries it.
 */
let lastMainLoopGap: { gapMs: number, atMs: number, cause: TMainLoopGapCause } | null = null

export function recordMainLoopGap(gap: { gapMs: number, atMs: number, cause: TMainLoopGapCause }): void {
  lastMainLoopGap = gap
}

let lastSampleContext: {
  visibleFrameId: string | null
  notifiedIdx: number
  workerNotifiedIdx: number
  frameElapsedMs: number | null
  frameDurationMs: number | null
  queueLength: number
  surfaceFrameCount: number
  pendingSurfaceCommit: boolean
  emptySurfaceStallMs: number | null
  visibleFrameExpectsVideo: boolean
  visibleVideoElementCount: number
  visibleVideoReadyState: number | null
  visibleVideoWidth: number | null
  visibleVideoNetworkState: number | null
  visibleVideoMediaErrorCode: number | null
  visibleVideoSrcScheme: TVisibleVideoSrcScheme | null
  videoPaintStallMs: number | null
  assetDownloadProgress01: number | null
  lastFrameTransitionAt: number
  lastTickAt: number
  documentVisible: boolean
  weeklyAvailabilitySuppressed: boolean
} = {
  visibleFrameId: null,
  notifiedIdx: -1,
  workerNotifiedIdx: -1,
  frameElapsedMs: null,
  frameDurationMs: null,
  queueLength: 0,
  surfaceFrameCount: 0,
  pendingSurfaceCommit: false,
  emptySurfaceStallMs: null,
  visibleFrameExpectsVideo: false,
  visibleVideoElementCount: 0,
  visibleVideoReadyState: null,
  visibleVideoWidth: null,
  visibleVideoNetworkState: null,
  visibleVideoMediaErrorCode: null,
  visibleVideoSrcScheme: null,
  videoPaintStallMs: null,
  assetDownloadProgress01: null,
  lastFrameTransitionAt: 0,
  lastTickAt: 0,
  documentVisible: true,
  weeklyAvailabilitySuppressed: false
}

function findFaultSlot(code: string): TFaultSlot | null {
  for (let i = 0; i < faultSlots.length; i++) {
    if (faultSlots[i]!.code === code) {
      return faultSlots[i]!
    }
  }
  for (let i = 0; i < faultSlots.length; i++) {
    if (!faultSlots[i]!.active && faultSlots[i]!.code === '') {
      return faultSlots[i]!
    }
  }
  return null
}

function setFaultActive(code: string, active: boolean, nowMs: number, detailMs: number): void {
  let slot = findFaultSlot(code)
  if (slot == null) {
    return
  }
  if (slot.code === '') {
    slot.code = code
  }
  if (active && !slot.active) {
    slot.active = true
    slot.sinceMs = nowMs
    slot.clearedAtMs = 0
    slot.detailMs = detailMs
    reportPlayerDiagnosticsFaultToSentry(code, detailMs)
    return
  }
  if (!active && slot.active) {
    slot.active = false
    slot.clearedAtMs = nowMs
    slot.detailMs = 0
    clearPlayerDiagnosticsFaultSentryDedupe(code)
    if (code === 'queue_duration_spine_mismatch') {
      clearPlayerPlaybackDurationInvariantSentryDedupe(code)
    }
  }
}

export function setPlayerDiagnosticFaultActive(
  code: string,
  active: boolean,
  nowMs: number,
  detailMs: number
): void {
  setFaultActive(code, active, nowMs, detailMs)
}

let diagnosticsWorker: Worker | null = null
let diagnosticsWorkerStarted = false

function handleDiagnosticsWorkerMessage(event: MessageEvent<{ type?: string, gapMs?: number }>): void {
  const type = event.data?.type
  if (type === 'ping') {
    diagnosticsWorker?.postMessage({ type: 'pong' })
    return
  }
  if (type === 'ping_missed') {
    const gapMs = event.data?.gapMs ?? 0
    setFaultActive('main_thread_ping_missed', true, Date.now(), gapMs)
    return
  }
  if (type === 'ping_recovered') {
    setFaultActive('main_thread_ping_missed', false, Date.now(), 0)
  }
}

export function initPlayerDiagnosticsWorker(): void {
  if (typeof Worker === 'undefined' || !isPlayerDiagnosticsSamplingEnabled()) {
    return
  }
  if (diagnosticsWorkerStarted) {
    return
  }
  diagnosticsWorkerStarted = true
  diagnosticsWorker = new PlayerDiagnosticsWorker()
  diagnosticsWorker.onmessage = handleDiagnosticsWorkerMessage
  diagnosticsWorker.postMessage({ type: 'start' })
}

export function disposePlayerDiagnosticsWorker(): void {
  if (diagnosticsWorker != null) {
    diagnosticsWorker.postMessage({ type: 'stop' })
    diagnosticsWorker.terminate()
    diagnosticsWorker = null
  }
  diagnosticsWorkerStarted = false
  setFaultActive('main_thread_ping_missed', false, Date.now(), 0)
}

/** performance.now()-derived ms (stalls, frame elapsed) are floats; snapshot schema requires integers. */
function roundedMs(ms: number | null): number | null {
  return ms == null ? null : Math.round(Math.max(0, ms))
}

function peekWebRecoveryReloadCounters(): { count: number, lastAt: number } {
  return {
    count: getWebRecoveryReloadCount(),
    lastAt: getWebRecoveryReloadLastAt()
  }
}

function applyDetector(code: string, suspected: boolean, nowMs: number, detailMs: number): void {
  // Single choke point for all detector detailMs: some (e.g. empty-surface / video-paint stall)
  // derive from performance.now() diffs (floats); fault.detailMs is schema .int().
  setFaultActive(code, suspected, nowMs, Math.round(Math.max(0, detailMs)))
}

/** True after {@link sampleContentProgressAlwaysOn} has written progress fields (kill-switch independent). */
let alwaysOnProgressSampled = false

/**
 * VBuffer unmounts when the queue is empty, weekly availability suppresses playback, etc.
 * Clear the local fault **and** keep the always-on latch so the next heartbeat still ships a slim
 * snapshot with `content_not_advancing` inactive. Omitting `playerDiagnostics` (null snapshot) leaves
 * `lastReportedPlayerDiagnostics` unchanged on the server while idle heartbeats keep `lastPlaybackAt`
 * fresh — which would sustain PLAYBACK_STALLED on an idle screen.
 */
export function clearContentNotAdvancingForPlaybackTeardown(nowMs = Date.now()): void {
  setFaultActive('content_not_advancing', false, nowMs, 0)
  alwaysOnProgressSampled = true
  lastSampleContext.visibleFrameId = null
  lastSampleContext.surfaceFrameCount = 0
  lastSampleContext.queueLength = 0
  lastSampleContext.frameDurationMs = null
  lastSampleContext.lastFrameTransitionAt = 0
}

export interface IContentProgressAlwaysOnInput {
  nowMs: number
  documentVisible: boolean
  queueLength: number
  surfaceFrameCount: number
  visibleFrameId: string | null
  visibleFrameChangedAtMs: number
  frameDurationMs: number
  msSinceLastLoopWrap: number | null
  lastLoopWrapAdvancedContent: boolean
  weeklyAvailabilitySuppressed: boolean
  suppressBecauseRegistrationResync: boolean
  suppressBecauseRegistrationTransitionHold: boolean
}

/**
 * Always-on progress sample for {@link content_not_advancing} + heartbeat slim snapshot (#1384).
 * Independent of the diagnostics kill switch so Studio/alerts see stalled content without a manual session.
 */
export function sampleContentProgressAlwaysOn(input: IContentProgressAlwaysOnInput): void {
  alwaysOnProgressSampled = true
  lastSampleContext.visibleFrameId = input.visibleFrameId
  lastSampleContext.queueLength = input.queueLength
  lastSampleContext.surfaceFrameCount = input.surfaceFrameCount
  lastSampleContext.frameDurationMs = input.frameDurationMs > 0 ? Math.round(input.frameDurationMs) : null
  lastSampleContext.lastFrameTransitionAt = input.visibleFrameChangedAtMs > 0
    ? Math.round(input.visibleFrameChangedAtMs)
    : 0
  lastSampleContext.documentVisible = input.documentVisible
  lastSampleContext.weeklyAvailabilitySuppressed = input.weeklyAvailabilitySuppressed

  const status = detectContentNotAdvancing({
    documentVisible: input.documentVisible,
    queueLength: input.queueLength,
    surfaceFrameCount: input.surfaceFrameCount,
    visibleFrameId: input.visibleFrameId,
    visibleFrameChangedAtMs: input.visibleFrameChangedAtMs,
    frameDurationMs: input.frameDurationMs,
    nowMs: input.nowMs,
    msSinceLastLoopWrap: input.msSinceLastLoopWrap,
    lastLoopWrapAdvancedContent: input.lastLoopWrapAdvancedContent,
    suppressBecauseRegistrationResync: input.suppressBecauseRegistrationResync,
    suppressBecauseRegistrationTransitionHold: input.suppressBecauseRegistrationTransitionHold,
    weeklyAvailabilitySuppressed: input.weeklyAvailabilitySuppressed
  })
  applyDetector('content_not_advancing', status.suspected, input.nowMs, status.dwellMs)
}

export function samplePlayerDiagnostics(input: IPlayerDiagnosticSampleInput): void {
  if (!isPlayerDiagnosticsSamplingEnabled(input.nowMs)) {
    return
  }

  lastSampleContext.visibleFrameId = input.visibleFrameId
  lastSampleContext.notifiedIdx = input.notifiedIdx
  lastSampleContext.workerNotifiedIdx = input.workerNotifiedIdx
  lastSampleContext.frameElapsedMs = input.frameElapsedMs
  lastSampleContext.frameDurationMs = input.frameDurationMs
  lastSampleContext.queueLength = input.queueLength
  lastSampleContext.surfaceFrameCount = input.surfaceFrameCount
  lastSampleContext.pendingSurfaceCommit = input.pendingSurfaceCommit
  // performance.now() diffs are floats; the snapshot schema requires integer ms
  // (.strict().int()) — an unrounded value 400s the command-result POST silently
  // (player only console.warns, never retries), so operators see getDiagnosticsSnapshot
  // time out instead of an error.
  lastSampleContext.emptySurfaceStallMs = roundedMs(input.emptySurfaceStallMs)
  lastSampleContext.visibleFrameExpectsVideo = input.visibleFrameExpectsVideo
  lastSampleContext.visibleVideoElementCount = input.visibleVideoElementCount
  lastSampleContext.visibleVideoReadyState = input.visibleVideoReadyState
  lastSampleContext.visibleVideoWidth = input.visibleVideoWidth
  lastSampleContext.visibleVideoNetworkState = input.visibleVideoNetworkState
  lastSampleContext.visibleVideoMediaErrorCode = input.visibleVideoMediaErrorCode
  lastSampleContext.visibleVideoSrcScheme = input.visibleVideoSrcScheme
  lastSampleContext.videoPaintStallMs = roundedMs(input.videoPaintStallMs)
  lastSampleContext.assetDownloadProgress01 = input.assetDownloadProgress01
  lastSampleContext.lastFrameTransitionAt = input.lastFrameTransitionAtMs
  lastSampleContext.lastTickAt = input.lastTickAtMs ?? 0
  lastSampleContext.documentVisible = input.documentVisible
  lastSampleContext.weeklyAvailabilitySuppressed = input.weeklyAvailabilitySuppressed

  const stuck = input.playbackTimerStuck
  applyDetector(
    'playback_timer_stuck',
    stuck?.suspected === true,
    input.nowMs,
    stuck?.overrunMs ?? 0
  )

  const frameDurationMs = input.frameDurationMs ?? 0
  const transition = detectFrameTransitionStall({
    nowMs: input.nowMs,
    documentVisible: input.documentVisible,
    queueLength: input.queueLength,
    frameDurationMs,
    visibleFrameStartMs: input.visibleFrameStartMs,
    suppressBecauseRegistrationResync: input.suppressBecauseRegistrationResync,
    suppressBecauseRegistrationTransitionHold: input.suppressBecauseRegistrationTransitionHold
  })
  applyDetector('frame_transition_stall', transition.suspected, input.nowMs, transition.overrunMs)

  const desync = detectSchedulerMainDesync({
    queueLength: input.queueLength,
    visibleFrameId: input.visibleFrameId,
    notifiedFrameId: input.notifiedFrameId,
    presentationNotifiedIdx: input.notifiedIdx,
    workerNotifiedIdx: input.workerNotifiedIdx,
    suppressBecauseRegistrationResync: input.suppressBecauseRegistrationResync,
    suppressBecauseRegistrationTransitionHold: input.suppressBecauseRegistrationTransitionHold
  })
  applyDetector('scheduler_main_desync', desync.suspected, input.nowMs, 0)

  const cadence = detectTickCadenceDegraded({
    nowMs: input.nowMs,
    documentVisible: input.documentVisible,
    queueLength: input.queueLength,
    lastTickAtMs: input.lastTickAtMs,
    suppressBecauseRegistrationResync: input.suppressBecauseRegistrationResync
  })
  applyDetector('tick_cadence_degraded', cadence.suspected, input.nowMs, cadence.gapMs)

  const emptySurface = detectEmptyPlaybackSurface({
    documentVisible: input.documentVisible,
    queueLength: input.queueLength,
    surfaceFrameCount: input.surfaceFrameCount,
    emptySurfaceStallMs: input.emptySurfaceStallMs,
    suppressBecauseRegistrationResync: input.suppressBecauseRegistrationResync,
    suppressBecauseRegistrationTransitionHold: input.suppressBecauseRegistrationTransitionHold,
    weeklyAvailabilitySuppressed: input.weeklyAvailabilitySuppressed
  })
  applyDetector('empty_playback_surface', emptySurface.suspected, input.nowMs, emptySurface.stallMs)

  const videoPaint = detectVideoPaintStall({
    documentVisible: input.documentVisible,
    queueLength: input.queueLength,
    surfaceFrameCount: input.surfaceFrameCount,
    visibleFrameExpectsVideo: input.visibleFrameExpectsVideo,
    videoPaintStallMs: input.videoPaintStallMs,
    suppressBecauseRegistrationResync: input.suppressBecauseRegistrationResync,
    suppressBecauseRegistrationTransitionHold: input.suppressBecauseRegistrationTransitionHold,
    weeklyAvailabilitySuppressed: input.weeklyAvailabilitySuppressed
  })
  applyDetector('video_paint_stall', videoPaint.suspected, input.nowMs, videoPaint.stallMs)

  const accelerated = detectFrameTransitionAccelerated({
    nowMs: input.nowMs,
    documentVisible: input.documentVisible,
    queueLength: input.queueLength,
    lastCompletedTransition: input.lastCompletedFrameTransition,
    suppressBecauseRegistrationResync: input.suppressBecauseRegistrationResync,
    suppressBecauseRegistrationTransitionHold: input.suppressBecauseRegistrationTransitionHold
  })
  applyDetector('frame_transition_accelerated', accelerated.suspected, input.nowMs, accelerated.earlyByMs)
  reportFrameTransitionAcceleratedToSentry(accelerated)
  // `content_not_advancing` is owned by the always-on poll ({@link sampleContentProgressAlwaysOn}),
  // which is the only caller with loop-wrap context — do not evaluate it a second time here.
}

export function buildPlayerDiagnosticsSnapshot(nowMs = Date.now()): TPlayerDiagnosticsSnapshot | null {
  const fullSampling = isPlayerDiagnosticsSamplingEnabled(nowMs)
  const webRecovery = peekWebRecoveryReloadCounters()
  const playlistSwap = peekPlaylistSwapDiagnosticsSample()
  const playlistSwapInteresting = isPlaylistSwapDiagnosticsInteresting(playlistSwap)
  /**
   * Slim always-on snapshot: progress fields + content_not_advancing / stale_presentation_peak
   * (#1384 / #1662). Also emit when web recovery reload counters are non-zero so Admin can see
   * hard_self_heal (#1532). Also emit while soft-deferred playlist swap / Feed-ahead so field
   * ops see identities without a session. Full fault catalog still requires the kill switch /
   * diagnostic session.
   */
  if (!fullSampling && !alwaysOnProgressSampled && webRecovery.count === 0 && !playlistSwapInteresting) {
    return null
  }

  const faults: TPlayerDiagnosticFault[] = []
  for (let i = 0; i < faultSlots.length; i++) {
    const slot = faultSlots[i]!
    if (slot.code === '') {
      continue
    }
    if (
      !fullSampling
      && slot.code !== 'content_not_advancing'
      && slot.code !== 'stale_presentation_peak'
    ) {
      continue
    }
    const fault: TPlayerDiagnosticFault = {
      code: slot.code,
      active: slot.active
    }
    if (slot.active && slot.sinceMs > 0) {
      fault.sinceMs = slot.sinceMs
    }
    if (!slot.active && slot.clearedAtMs > 0) {
      fault.clearedAtMs = slot.clearedAtMs
    }
    if (slot.detailMs > 0) {
      fault.detailMs = slot.detailMs
    }
    faults.push(fault)
    if (faults.length >= PLAYER_DIAGNOSTIC_FAULTS_MAX_LENGTH) {
      break
    }
  }

  // Avoid spamming empty slim snapshots when nothing is painted and no stall fault is active.
  // Still emit when web recovery counters are present (hard_self_heal visibility).
  // Still emit for pending swap / Feed-ahead (playlist identity visibility).
  if (
    !fullSampling
    && faults.length === 0
    && webRecovery.count === 0
    && !playlistSwapInteresting
    && (lastSampleContext.visibleFrameId == null || lastSampleContext.visibleFrameId === '')
  ) {
    return null
  }

  const sessionUntil = getPlayerDiagnosticSessionActiveUntilMs()
  const snapshot: TPlayerDiagnosticsSnapshot = {
    schemaVersion: PLAYER_DIAGNOSTICS_SNAPSHOT_SCHEMA_VERSION,
    collectedAt: nowMs,
    faults
  }

  if (lastSampleContext.visibleFrameId != null && lastSampleContext.visibleFrameId !== '') {
    snapshot.visibleFrameId = lastSampleContext.visibleFrameId
  }
  if (lastSampleContext.notifiedIdx >= 0) {
    snapshot.notifiedIdx = lastSampleContext.notifiedIdx
  }
  if (lastSampleContext.workerNotifiedIdx >= 0) {
    snapshot.workerNotifiedIdx = lastSampleContext.workerNotifiedIdx
  }
  if (lastSampleContext.frameElapsedMs != null) {
    // frameElapsedMs derives from the loop clock (performance.now()-based), so it's fractional
    // on virtually every sample — unlike frameDurationMs (segment t1-t0, already integer ms).
    snapshot.frameElapsedMs = roundedMs(lastSampleContext.frameElapsedMs) ?? undefined
  }
  if (lastSampleContext.frameDurationMs != null) {
    snapshot.frameDurationMs = lastSampleContext.frameDurationMs
  }
  if (lastSampleContext.queueLength >= 0) {
    snapshot.queueLength = lastSampleContext.queueLength
  }
  if (lastSampleContext.surfaceFrameCount >= 0) {
    snapshot.surfaceFrameCount = lastSampleContext.surfaceFrameCount
  }
  snapshot.pendingSurfaceCommit = lastSampleContext.pendingSurfaceCommit
  if (lastSampleContext.emptySurfaceStallMs != null) {
    snapshot.emptySurfaceStallMs = lastSampleContext.emptySurfaceStallMs
  }
  snapshot.visibleFrameExpectsVideo = lastSampleContext.visibleFrameExpectsVideo
  if (lastSampleContext.visibleVideoElementCount >= 0) {
    snapshot.visibleVideoElementCount = lastSampleContext.visibleVideoElementCount
  }
  if (lastSampleContext.visibleVideoReadyState != null) {
    snapshot.visibleVideoReadyState = lastSampleContext.visibleVideoReadyState
  }
  if (lastSampleContext.visibleVideoWidth != null) {
    snapshot.visibleVideoWidth = lastSampleContext.visibleVideoWidth
  }
  if (lastSampleContext.visibleVideoNetworkState != null) {
    snapshot.visibleVideoNetworkState = lastSampleContext.visibleVideoNetworkState
  }
  if (lastSampleContext.visibleVideoMediaErrorCode != null) {
    snapshot.visibleVideoMediaErrorCode = lastSampleContext.visibleVideoMediaErrorCode
  }
  if (lastSampleContext.visibleVideoSrcScheme != null) {
    snapshot.visibleVideoSrcScheme = lastSampleContext.visibleVideoSrcScheme
  }
  if (lastSampleContext.videoPaintStallMs != null) {
    snapshot.videoPaintStallMs = lastSampleContext.videoPaintStallMs
  }
  if (lastSampleContext.assetDownloadProgress01 != null) {
    snapshot.assetDownloadProgress01 = lastSampleContext.assetDownloadProgress01
  }
  if (lastMainLoopGap != null) {
    snapshot.lastMainLoopGapMs = Math.round(lastMainLoopGap.gapMs)
    snapshot.lastMainLoopGapAt = Math.round(lastMainLoopGap.atMs)
    snapshot.lastMainLoopGapCause = lastMainLoopGap.cause
  }
  if (lastSampleContext.lastFrameTransitionAt > 0) {
    snapshot.lastFrameTransitionAt = Math.round(lastSampleContext.lastFrameTransitionAt)
  }
  if (lastSampleContext.lastTickAt > 0) {
    snapshot.lastTickAt = Math.round(lastSampleContext.lastTickAt)
  }
  snapshot.documentVisible = lastSampleContext.documentVisible
  snapshot.weeklyAvailabilitySuppressed = lastSampleContext.weeklyAvailabilitySuppressed
  if (sessionUntil > nowMs) {
    snapshot.diagnosticSessionActiveUntil = Math.round(sessionUntil)
  }

  // Device Owner crash/OOM self-heal (issue #1369) — cached from native heartbeat / status.
  const selfHeal = getCachedAndroidProcessSelfHealStatus()
  if (selfHeal != null) {
    snapshot.nativeSelfHealRestartCount = Math.round(selfHeal.restartAttemptsInWindow)
    if (selfHeal.lastRestartAt > 0) {
      snapshot.nativeSelfHealLastRestartAt = Math.round(selfHeal.lastRestartAt)
    }
  }

  // WebView recovery reload counters (sessionStorage) — platform unhappy `hard_self_heal` (#1532).
  if (webRecovery.count > 0) {
    snapshot.webRecoveryReloadCount = webRecovery.count
  }
  if (webRecovery.lastAt > 0) {
    snapshot.webRecoveryReloadLastAt = webRecovery.lastAt
  }

  const aspect = peekLastPlaybackAspectTelemetrySample()
  if (aspect != null) {
    snapshot.viewportAspect = aspect.viewportAspect
    snapshot.effectiveViewportWidthPx = aspect.effectiveWidthPx
    snapshot.effectiveViewportHeightPx = aspect.effectiveHeightPx
    snapshot.screenRotation = aspect.screenRotation
    snapshot.aspectRestrictActive = aspect.aspectRestrictActive
    if (aspect.onAirFrameLayoutAspect != null) {
      snapshot.onAirFrameLayoutAspect = aspect.onAirFrameLayoutAspect
    }
    if (aspect.onAirFrameLayoutWidthPx != null) {
      snapshot.onAirFrameLayoutWidthPx = aspect.onAirFrameLayoutWidthPx
    }
    if (aspect.onAirFrameLayoutHeightPx != null) {
      snapshot.onAirFrameLayoutHeightPx = aspect.onAirFrameLayoutHeightPx
    }
    if (aspect.visibleMediaAspect != null) {
      snapshot.visibleMediaAspect = aspect.visibleMediaAspect
    }
    if (aspect.visibleMediaWidthPx != null) {
      snapshot.visibleMediaWidthPx = aspect.visibleMediaWidthPx
    }
    if (aspect.visibleMediaHeightPx != null) {
      snapshot.visibleMediaHeightPx = aspect.visibleMediaHeightPx
    }
    const signals = unexpectedVerticalAspectSignals(aspect)
    if (signals.length > 0) {
      snapshot.verticalAspectSignals = signals.join(',')
    }
  }

  if (playlistSwap != null) {
    if (playlistSwap.feedPlaylistIdentity != null && playlistSwap.feedPlaylistIdentity !== '') {
      snapshot.feedPlaylistIdentity = playlistSwap.feedPlaylistIdentity.slice(0, PLAYER_DIAGNOSTIC_FRAME_ID_MAX_LENGTH)
    }
    if (playlistSwap.materializedPlaylistIdentity != null && playlistSwap.materializedPlaylistIdentity !== '') {
      snapshot.materializedPlaylistIdentity = playlistSwap.materializedPlaylistIdentity.slice(
        0,
        PLAYER_DIAGNOSTIC_FRAME_ID_MAX_LENGTH
      )
    }
    if (playlistSwap.pendingPlaylistSwapPhase != null) {
      snapshot.pendingPlaylistSwapPhase = playlistSwap.pendingPlaylistSwapPhase
    }
    if (playlistSwap.pendingPlaylistSwapProgress01 != null) {
      snapshot.pendingPlaylistSwapProgress01 = playlistSwap.pendingPlaylistSwapProgress01
    }
    if (playlistSwap.pendingPlaylistSwapTitle != null && playlistSwap.pendingPlaylistSwapTitle !== '') {
      snapshot.pendingPlaylistSwapTitle = playlistSwap.pendingPlaylistSwapTitle.slice(0, 128)
    }
    if (playlistSwap.pendingPlaylistSwapFlushReason != null) {
      snapshot.pendingPlaylistSwapFlushReason = playlistSwap.pendingPlaylistSwapFlushReason
    }
    if (playlistSwap.pendingPlaylistSwapArmedAtMs != null) {
      snapshot.pendingPlaylistSwapArmedAtMs = playlistSwap.pendingPlaylistSwapArmedAtMs
    }
    if (playlistSwap.pendingPlaylistSwapReadyAtMs != null) {
      snapshot.pendingPlaylistSwapReadyAtMs = playlistSwap.pendingPlaylistSwapReadyAtMs
    }
    if (playlistSwap.pendingPlaylistSwapFlushStartedAtMs != null) {
      snapshot.pendingPlaylistSwapFlushStartedAtMs = playlistSwap.pendingPlaylistSwapFlushStartedAtMs
    }
    if (playlistSwap.pendingPlaylistSwapFlushEndedAtMs != null) {
      snapshot.pendingPlaylistSwapFlushEndedAtMs = playlistSwap.pendingPlaylistSwapFlushEndedAtMs
    }
    snapshot.feedAheadOfMaterialization = playlistSwap.feedAheadOfMaterialization
  }

  const glass = peekPlayerGlassDiagnosticsSample()
  if (glass.glassState != null) {
    snapshot.glassState = glass.glassState
  }
  if (glass.skippedUncachedFrameCount > 0) {
    snapshot.skippedUncachedFrameCount = glass.skippedUncachedFrameCount
  }

  return snapshot
}

export function clearPlayerDiagnosticEvents(): void {
  for (let i = 0; i < faultSlots.length; i++) {
    faultSlots[i]!.code = ''
    faultSlots[i]!.active = false
    faultSlots[i]!.sinceMs = 0
    faultSlots[i]!.clearedAtMs = 0
    faultSlots[i]!.detailMs = 0
  }
}

export function runPlayerDiagnosticsSafely(fn: () => void): void {
  try {
    fn()
  }
  catch (err) {
    console.warn(JSON.stringify({
      tag: 'player_diagnostics_isolated_error',
      message: err instanceof Error ? err.message : String(err)
    }))
  }
}

export function resetPlayerDiagnosticsStateForTests(): void {
  disposePlayerDiagnosticsWorker()
  clearPlayerDiagnosticEvents()
  alwaysOnProgressSampled = false
  lastSampleContext = {
    visibleFrameId: null,
    notifiedIdx: -1,
    workerNotifiedIdx: -1,
    frameElapsedMs: null,
    frameDurationMs: null,
    queueLength: 0,
    surfaceFrameCount: 0,
    pendingSurfaceCommit: false,
    emptySurfaceStallMs: null,
    visibleFrameExpectsVideo: false,
    visibleVideoElementCount: 0,
    visibleVideoReadyState: null,
    visibleVideoWidth: null,
    visibleVideoNetworkState: null,
    visibleVideoMediaErrorCode: null,
    visibleVideoSrcScheme: null,
    videoPaintStallMs: null,
    assetDownloadProgress01: null,
    lastFrameTransitionAt: 0,
    lastTickAt: 0,
    documentVisible: true,
    weeklyAvailabilitySuppressed: false
  }
}
